import React from 'react';
import * as Scrivito from 'scrivito';
import Navigation from '../../components/Navigation';
import Footer from '../../components/Footer';
import CookieNotice from '../../components/CookieNotice';
import SeoHead from '../../components/SeoHead';
import { currentOrigin, toAbsoluteUrl } from '../../lib/origin';
import { findHomepage } from '../../lib/homepage';

Scrivito.provideObjClass('Page', {
  attributes: {
    title: 'string',
    body: 'widgetlist',
    childOrder: 'referencelist',
    isoCodeLanguage: 'string',
    metaTags: 'string',
    metaDescription: 'string',
  },
});

// The Rails layout gave every page the same <title> and printed the page's own
// title only as an <h1>. The browser tab now gets both, so legal/sub pages are
// distinguishable in tabs, history and search results instead of being
// identical to every other page on the site.
const SITE_TITLE = 'trans-tech - translations technical';

const PageComponent = Scrivito.connect(({ page }) => {
  const metaDescription = page.get('metaDescription');
  const metaTags = page.get('metaTags');
  // The legal sub-pages never had this set - it only ever mattered once
  // these actually got rendered (see api/prerender.mjs), and up to now
  // silently fell back to English regardless of which language's Homepage
  // the page belongs under. Its own value (once an editor sets it) still
  // wins; this only fills the gap.
  const isoCode = page.get('isoCodeLanguage') || findHomepage(page)?.get('isoCodeLanguage') || 'en';
  const pageTitle = page.get('title');

  // These are exactly the legal/imprint sub-pages that an SEO/GEO audit
  // flagged for having no structured data at all - unlike Homepage (see
  // ../Homepage), which declares an Organization schema, nothing here told a
  // machine what kind of page this even is. A plain WebPage type, naming the
  // same Organization as its publisher, is the minimum that closes that gap
  // for every page using this template - not just the ones an editor
  // remembers to special-case.
  const origin = currentOrigin();
  const webPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: pageTitle || SITE_TITLE,
    inLanguage: isoCode,
    url: toAbsoluteUrl(Scrivito.urlFor(page), origin),
    isPartOf: { '@type': 'Organization', name: 'trans-tech', url: origin },
    ...(metaDescription && { description: metaDescription }),
  };

  return (
    <>
      <SeoHead
        page={page}
        lang={isoCode}
        title={pageTitle ? `${pageTitle} | ${SITE_TITLE}` : SITE_TITLE}
        description={metaDescription}
        keywords={metaTags}
        ogImage="/bg-1.jpg"
        structuredData={webPageSchema}
      />

      <Navigation page={page} />
      {/* Rendered from the layout in Rails, so it appeared on the legal pages
          too, not only on the homepage. */}
      <CookieNotice page={page} />

      {/* app/views/page/index.html.haml was .container > h1 + body – without the
          container the legal text ran the full window width, and the heading was
          missing entirely. */}
      <main className="container">
        <Scrivito.ContentTag tag="h1" attribute="title" content={page} />
        <Scrivito.ContentTag tag="div" attribute="body" content={page} />
      </main>

      <Footer page={page} />
    </>
  );
});

Scrivito.provideComponent('Page', PageComponent);

export default PageComponent;
